/* eslint-disable no-unused-vars */
import { cloneDeep, findIndex } from 'lodash';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import HOTSPOT_TYPE from 'consts/hotspotType.const';
import MEDIA_TYPE from 'consts/mediaType.const';

import MapFloorPlan from 'components/MobiHomeLayout/MobiController/MapFloorPlan';
import './style.scss';
import { moveItemToTop } from 'containers/menuV2';
import ViewPointMapPlan from 'components/MobiHomeLayout/MobiController/MapFloorPlan/ViewPointMapPlan';
import { useDispatch } from 'react-redux';
import { setOpenPopUp } from 'store/actions';

const MapScenePreview = ({
  media,
  menuList,
  scenes,
  hotspotHistory,
  tour,
  selectSceneById,
  currentPano,
}) => {
  const cloneMedia = cloneDeep(media);
  const menus = cloneDeep(menuList);
  const dispatch = useDispatch();

  const [imgIndex, setImgIndex] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [zoom, setZoom] = useState(0);
  const [applyScale, setApplyScale] = useState(1);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const tranformRef = useRef(null);
  const onScrolling = useRef(false);
  const container = useRef();
  const sliderRef = useRef();
  const isDown = useRef(false);
  const startX = useRef();
  const scrollLeft = useRef();

  const allScenes = useMemo(() => {
    let res = [];
    menus.forEach((menu) => {
      res.push(...menu.scenes);
    });
    return res;
  }, [menus]);

  const { image } = useMemo(
    () => media.mapFloorPlan[imgIndex],
    [imgIndex, media.mapFloorPlan]
  );

  const newMedia = useMemo(() => {
    return {
      ...cloneMedia,
      mapFloorPlan: {
        ...cloneMedia.mapFloorPlan[imgIndex],
        objects: cloneMedia.mapFloorPlan[imgIndex].objects.map((obj, index) => {
          const mapIndexWithMenu = findIndex(
            allScenes,
            (e) => e._id === obj.id
          );
          if (mapIndexWithMenu > -1) {
            obj.index = mapIndexWithMenu;
          } else {
            obj.index = index;
          }
          return obj;
        }),
      },
    };
  }, [allScenes, cloneMedia, imgIndex]);

  const onResize = useCallback(() => {
    if (container.current && (!size.width || !size.height)) {
      const { width, height } = container.current.getBoundingClientRect();
      const containerRatio = width / height;
      const imgRatio = image.width / image.height;
      const divSize = { width: 0, height: 0 };
      if (containerRatio > imgRatio) {
        // should take width of container
        divSize.width = width;
        divSize.height = width / imgRatio;
      } else {
        // should take height of container
        divSize.height = height;
        divSize.width = imgRatio * height;
      }

      setSize(divSize);
    }
  }, [image, size]);

  const handleMapListSlider = (e) => {
    isDown.current = true;
    setTimeout(() => (onScrolling.current = true), 200);
    sliderRef.current.classList.add('active');
    startX.current = e.pageX - sliderRef.current.offsetLeft;
    scrollLeft.current = sliderRef.current.scrollLeft;
  };

  const handleSlideMapList = (e) => {
    if (!isDown.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const moving = (x - startX.current) * 1.5;
    sliderRef.current.scrollLeft = scrollLeft.current - moving;
  };

  const handleStopMapListSlider = () => {
    isDown.current = false;
    setTimeout(() => (onScrolling.current = false), 200);
    sliderRef.current.classList.remove('active');
    setShowDetail(false);
  };

  const handleChangeImage = (index) => {
    !onScrolling.current && setImgIndex(index);
  };

  const handleResetTranform = () => {
    tranformRef.current.centerView(applyScale, 0);
  };

  const handleZoomIn = () => {
    tranformRef.current.zoomIn();
    setTimeout(() => {
      setZoom(tranformRef.current.state.scale);
    }, 400);
  };

  const handleZoomOut = () => {
    tranformRef.current.zoomOut();
    setTimeout(() => {
      setZoom(tranformRef.current.state.scale);
    }, 400);
  };

  const handleSelectScene = (...rest) => {
    selectSceneById(...rest);
    dispatch(setOpenPopUp(false));
  };

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  // useEffect(() => {
  //   setTimeout(() => {
  //     if (container.current) {
  //       const { width: containerW, height: containerH } =
  //         container.current.childNodes[0].getBoundingClientRect();
  //       const { width, height } = image;
  //       const initScale = containerW / width;
  //       const initHeight = height * initScale;
  //       const imgW = containerW,
  //         imgH = initHeight;
  //       const containerRatio = containerW / containerH;
  //       const imgRatio = imgW / imgH;

  //       if (containerRatio > imgRatio) {
  //         setApplyScale(1);
  //         tranformRef.current.centerView(1, 0);
  //       } else {
  //         setApplyScale(containerH / imgH);
  //         tranformRef.current.centerView(containerH / imgH, 0);
  //       }
  //     }
  //   }, 100);
  // }, [container, image]);

  // useEffect(() => {
  //   setTimeout(() => {
  //     tranformRef.current.centerView(applyScale, 0);
  //   }, 100);
  // }, [applyScale]);

  const floorMaps = useMemo(() => {
    const mapFloorPlansInHotspot =
      currentPano?.hotspots?.reduce((plans, hs) => {
        let mapPlansInCurrentHotspot = [];
        // get mapFloorPlan array in hotspot OR in media object
        if (
          hs?.type === HOTSPOT_TYPE.MEDIA &&
          hs?.media?.type === MEDIA_TYPE.MAPFLOORPLAN &&
          hs?.media?.mapFloorPlan?.length
        ) {
          mapPlansInCurrentHotspot = hs.media.mapFloorPlan;
        } else if (
          hs?.type === HOTSPOT_TYPE.MAP_FLOOR_PLAN &&
          hs?.mapFloorPlan?.length
        ) {
          mapPlansInCurrentHotspot = hs.mapFloorPlan;
        }

        // get unique floorplan
        const filterMapPlansInCurrentHotspot = mapPlansInCurrentHotspot.reduce(
          (plansInHs, currPlan) => {
            const existed = plans.find(
              (plan) =>
                plan.image.name === currPlan.image.name &&
                plan.image.url === currPlan.image.url
            );
            if (!existed) {
              plansInHs.push(currPlan);
            }
            return plansInHs;
          },
          []
        );

        return plans.concat(filterMapPlansInCurrentHotspot);
      }, []) || [];

    return moveItemToTop(mapFloorPlansInHotspot, currentPano?._id);
  }, [currentPano]);

  const mapShowed = useMemo(() => {
    if (floorMaps.length > 0 && currentPano) {
      const getCurrentMap = (floorMaps, pano) => {
        return floorMaps.find((item) =>
          item.objects.length
            ? item.objects.some((obj) => obj.id === pano._id)
            : item
        );
      };

      return getCurrentMap(floorMaps, currentPano);
    } else return;
  }, [floorMaps, currentPano]);

  return (
    <>
      <div
        className="container-2d-map"
        ref={container}
        style={{ width: '100%', height: '100vh' }}
      >
        {/* <TransformContainer
          tour={tour}
          size={1}
          zoom={zoom}
          media={media}
          imgRoot={image}
          scenes={scenes}
          setZoom={setZoom}
          newMedia={newMedia}
          tranformRef={tranformRef}
          hotspotHistory={hotspotHistory}
          container={container}
          selectSceneById={selectSceneById}
        />
        <ul
          ref={sliderRef}
          className="map-2d__list"
          onPointerDown={handleMapListSlider}
          onPointerLeave={handleStopMapListSlider}
          onPointerUp={handleStopMapListSlider}
          onPointerMove={handleSlideMapList}
        >
          {media.mapFloorPlan &&
            media.mapFloorPlan.map((item, index) => (
              <li
                key={index}
                className={`${index === imgIndex ? 'active' : ''} ${
                  showDetail ? 'show-detail' : ''
                }`}
                onPointerDown={() => setShowDetail(index === imgIndex)}
              >
                <button onClick={() => handleChangeImage(index)}>
                  <span>{item.image.name}</span>
                </button>
                <p>{item.image.name}</p>
              </li>
            ))}
        </ul> */}

        {/* <div style={{ height: '100%' }} className="map_image">
          <img
            src={media.mapFloorPlan[0].image.url}
            alt={media.mapFloorPlan[0].image.name}
            style={{
              width: '100%',
              height: '100%',
              opacity: 1,
              objectFit: 'contain',
            }}
          />
          {media.mapFloorPlan[0].objects.map((viewpoint) => {
            const currentPoint = viewpoint.id === currentPano?._id;
            const [left, top] = viewpoint.coordinates;
            const leftInPercent = left * 100;
            const topInPercent = top * 100;
            const currentScene =
              scenes.find((s) => s._id === viewpoint.id) || {};

            return (
              <div
                key={viewpoint.id}
                className={`viewpoint-container ${
                  currentPoint ? 'current-viewpoint' : 'viewpoint'
                } `}
                style={{
                  left: `${leftInPercent}%`,
                  top: `${topInPercent}%`,
                  position: 'absolute',
                }}
                // onClick={() => onSelect(currentScene.groupId, currentScene.id)}
              >
                <p className="scene-title">{currentScene.title}</p>
              </div>
            );
          })}
        </div> */}

        <div
          className={`map-floorplan-container open`}
          style={{
            opacity: 1,
            right: '50%',
            top: '50%',
            transform: 'translate(50%, -50%)',
          }}
        >
          <img
            src={mapShowed?.image.url}
            alt={mapShowed?.image.name}
            style={{ opacity: 1 }}
          />
          {mapShowed?.objects?.map((viewpoint) => {
            return (
              <React.Fragment key={viewpoint.id}>
                <ViewPointMapPlan
                  viewpoint={viewpoint}
                  currentPano={currentPano}
                  selectSceneById={handleSelectScene}
                />
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {/* <div className="map2d-btn-zoom">
        <div className="map2d-btn-zoom--inout">
          <CIcon icon={cilZoomOut} onClick={handleZoomOut} />
          <CIcon onClick={handleResetTranform} icon={cilSync} />
          <CIcon icon={cilZoomIn} onClick={handleZoomIn} />
        </div>
      </div> */}
    </>
  );
};

export default memo(MapScenePreview);
